import { useMemo, useState } from "react";
import { randomId } from "@/shared/utils/randomId";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, Sprout } from "lucide-react";
import { useInputLookup } from "@/features/inventory/hooks/useInputs";
import type { Input as InventoryInput } from "@/features/inventory/services/inputsApi";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useCreateRequisition } from "@/features/inventory/hooks/useRequisition";
import { useDepartments } from "@/shared/hooks/useDepartments";
import { useNotification } from "@/shared/hooks/useNotification";
import { useApiError } from "@/shared/hooks/useApiError";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { useSeedBatchesByOrder } from "../../hooks/useSeedBatches";
import type { SeedBatchDto } from "../../services/seedBatchApi";

// Mirror of PhaseWorkflowDialogs/ReservedOrdersPage — UnitOfMeasure enum values
// the sowing pipeline uses for tray/piece quantities.
const SOWING_UNIT_TRAYS = 11;
const SOWING_UNIT_PIECES = 5;

const getSowingUnitLabel = (unit: number | undefined, quantity: number) => {
  const isSingular = Math.abs(quantity) === 1;
  if (unit === SOWING_UNIT_PIECES) return isSingular ? "piece" : "pieces";
  if (unit === SOWING_UNIT_TRAYS) return isSingular ? "tray" : "trays";
  return isSingular ? "unit" : "units";
};

// Conversion ratios to a per-category canonical base. Lets us convert qty entered
// in any unit (kg, g, mg, etc.) to the input's native unit for stock comparison.
// Categories outside Mass/Volume don't convert across units.
const TO_GRAMS: Record<string, number> = {
  Grams: 1,
  Kilograms: 1000,
  Milligrams: 0.001,
};
const TO_MILLILITERS: Record<string, number> = {
  Milliliters: 1,
  Liters: 1000,
};

const convertQty = (
  value: number,
  fromUnit: string,
  toUnit: string,
  category: string
): number => {
  if (fromUnit === toUnit) return value;
  if (category === "Mass" && TO_GRAMS[fromUnit] && TO_GRAMS[toUnit]) {
    return (value * TO_GRAMS[fromUnit]) / TO_GRAMS[toUnit];
  }
  if (
    category === "Volume" &&
    TO_MILLILITERS[fromUnit] &&
    TO_MILLILITERS[toUnit]
  ) {
    return (value * TO_MILLILITERS[fromUnit]) / TO_MILLILITERS[toUnit];
  }
  // No conversion for Count and other categories — units must match.
  return value;
};

interface SowingRequisitionDialogProps {
  instruction: SowingInstruction & { instructionNumber?: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface FormItem {
  tempId: string;
  // null for customer-seed lines — those reference seedBatchId instead. The backend
  // requires exactly one of inputId/seedBatchId.
  inputId: string | null;
  inputName: string;
  quantityRequested: number;
  unitOfMeasure: string;
  unitOfMeasureDisplay: string;
  isExternalItem: boolean;
  // Set when the row represents the customer's seed batch (instruction.seedBatchId).
  // Locks the row so it can't be removed and signals the backend via SeedBatchId.
  seedBatchId?: string;
  seedBatchNumber?: string;
  seedBatchCustomerName?: string;
  seedBatchAvailableQuantity?: number;
  seedBatchLotNumber?: string;
  seedBatchExpiryDate?: string;
}

const APPROVAL_APPROVED = 2;

const isApprovedSeedBatch = (batch: SeedBatchDto) =>
  batch.approvalStatus === APPROVAL_APPROVED || batch.approvalStatus === "Approved";

const normalizeText = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const formatQty = (value?: number | null) =>
  Number(value ?? 0).toLocaleString(undefined, {
    maximumFractionDigits: 4,
  });

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const SowingRequisitionDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: SowingRequisitionDialogProps) => {
  const { user } = useAuthStore();
  const [inputSearchValue, setInputSearchValue] = useState("");
  const [inputSearchTerm, setInputSearchTerm] = useState("");
  const [chosenInput, setChosenInput] = useState<InventoryInput | null>(null);
  const debouncedInputSearch = useDebounce(inputSearchTerm.trim(), 300);
  const inputsQuery = useInputLookup(open, debouncedInputSearch);
  const departmentsQuery = useDepartments();
  const unitsQuery = useUnitsOfMeasure();
  const seedBatchesByOrderQuery = useSeedBatchesByOrder(instruction.orderId);
  const createRequisition = useCreateRequisition();
  const notify = useNotification();
  const handleApiError = useApiError();

  const defaultRequiredBy = (() => {
    const d = new Date(instruction.scheduledSowingDate);
    if (!isNaN(d.getTime())) {
      // 1 day before scheduled sowing as a sensible default
      d.setDate(d.getDate() - 1);
      return d.toISOString().split("T")[0];
    }
    return new Date().toISOString().split("T")[0];
  })();

  const [requiredByDate, setRequiredByDate] = useState(defaultRequiredBy);
  const [departmentId, setDepartmentId] = useState(user?.departmentId ?? "");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<FormItem[]>([]);
  const [selectedSeedQuantities, setSelectedSeedQuantities] = useState<Record<string, string>>({});

  const [draft, setDraft] = useState({
    inputId: "",
    quantityRequested: "",
    qtyUnit: "",              // user-selected unit for Total Qty (defaults to input's native)
    procureExternally: false, // user opts in to splitting off an external portion
    externalQty: "",          // qty to procure externally
    externalUnit: "",         // user-selected unit for External qty
  });
  const searchPending = inputSearchTerm.trim() !== debouncedInputSearch;
  const filteredInputs = searchPending
    ? []
    : inputsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const selectedInput = chosenInput?.id === draft.inputId ? chosenInput : null;
  const instructionCustomerName = instruction.customerName?.trim() || "Unknown customer";
  const customerSeedBatches = useMemo(() => {
    const batches = seedBatchesByOrderQuery.data ?? [];
    if (!instruction.seedBatchId) return [];

    const matching = batches.filter((batch) => {
      const cropMatches =
        !instruction.cropTypeId || batch.cropTypeId === instruction.cropTypeId;
      const varietyMatches =
        !instruction.variety ||
        normalizeText(batch.variety) === normalizeText(instruction.variety);
      return cropMatches && varietyMatches && isApprovedSeedBatch(batch);
    });

    if (matching.some((batch) => batch.id === instruction.seedBatchId)) {
      return matching;
    }

    return matching;
  }, [
    instruction.cropTypeId,
    instruction.seedBatchId,
    instruction.variety,
    seedBatchesByOrderQuery.data,
  ]);
  const fallbackSeedBatch: SeedBatchDto | null =
    instruction.seedBatchId && customerSeedBatches.length === 0
      ? {
          id: instruction.seedBatchId,
          customerName: instruction.customerName,
          ownerName: instruction.customerName,
          batchNumber: instruction.seedBatchNumber ?? "Seed batch",
          receivedDate: "",
          cropTypeId: instruction.cropTypeId ?? "",
          cropTypeName: instruction.cropTypeName ?? "",
          variety: instruction.variety,
          numberOfPacks: 0,
          quantityPerPack: 0,
          quantityReceived: instruction.seedBatchAvailableQuantity ?? 0,
          quantityAvailable: instruction.seedBatchAvailableQuantity ?? 0,
          unit: instruction.seedBatchUnit ?? "",
          approvalStatus: "Approved",
          createdAt: "",
        }
      : null;
  const visibleSeedBatches = fallbackSeedBatch
    ? [fallbackSeedBatch]
    : customerSeedBatches;
  const selectedSeedBatches = visibleSeedBatches.filter((batch) => {
    const raw = selectedSeedQuantities[batch.id];
    return raw != null && (parseFloat(raw) || 0) > 0;
  });
  const selectedSeedTotals = selectedSeedBatches.reduce<Record<string, number>>(
    (acc, batch) => {
      const unit = batch.unit || "-";
      acc[unit] = (acc[unit] ?? 0) + (parseFloat(selectedSeedQuantities[batch.id]) || 0);
      return acc;
    },
    {}
  );
  // Input.unitOfMeasure comes from the API as the enum's numeric value (e.g. 1),
  // not its name. Look up by value so we can resolve the canonical name used by
  // the conversion tables and the unit dropdown's category filter.
  const nativeUnitRaw = selectedInput?.unitOfMeasure ?? "";
  const nativeUnitMeta = unitsQuery.data?.find(
    (u) => String(u.value) === String(nativeUnitRaw) || u.name === nativeUnitRaw
  );
  const nativeUnitName = nativeUnitMeta?.name ?? "";
  const nativeUnitDisplay =
    selectedInput?.unitOfMeasureDisplay ||
    nativeUnitMeta?.abbreviation ||
    nativeUnitName;
  // Filter unit dropdown to compatible units only (same category as the input's native).
  const compatibleUnits = unitsQuery.data?.filter(
    (u) => u.category === nativeUnitMeta?.category
  ) ?? [];
  const category = nativeUnitMeta?.category ?? "";

  // Effective unit for each field — fall back to the input's native unit until user picks one.
  const totalUnit = draft.qtyUnit || nativeUnitName;
  const externalUnit = draft.externalUnit || nativeUnitName;

  const rawDraftQty = parseFloat(draft.quantityRequested) || 0;
  const rawExternalQty = parseFloat(draft.externalQty) || 0;

  // Convert everything to the input's native unit so stock comparisons are honest.
  const draftQtyNative = convertQty(rawDraftQty, totalUnit, nativeUnitName, category);
  const externalQtyNative = draft.procureExternally
    ? Math.max(
        0,
        Math.min(
          draftQtyNative,
          convertQty(rawExternalQty, externalUnit, nativeUnitName, category)
        )
      )
    : 0;
  const internalQtyNative = Math.max(0, draftQtyNative - externalQtyNative);

  // Stock the sowing manager has already drafted for this input within the form.
  const sameInputDraftedQty = selectedInput
    ? items
        .filter((i) => i.inputId === selectedInput.id)
        .reduce((s, i) => s + i.quantityRequested, 0)
    : 0;
  const stockAvailable = selectedInput?.currentStock ?? 0;
  const autoShortfall = selectedInput
    ? Math.max(0, sameInputDraftedQty + draftQtyNative - stockAvailable)
    : 0;
  const isShort = autoShortfall > 0;

  const handleAddItem = () => {
    if (!draft.inputId || !draft.quantityRequested) {
      notify.warning("Pick an input and enter a quantity");
      return;
    }
    if (!selectedInput) return;

    // Items are stored in the input's native unit so the rest of the form (preview,
    // submit payload, stock comparison) is consistent regardless of what unit the
    // user typed in.
    const unitDisplay = nativeUnitDisplay;
    const baseRow = {
      inputId: draft.inputId,
      inputName: selectedInput.inputName,
      // Send the raw enum value the backend issued — the resolved name is used
      // only for client-side conversion/display.
      unitOfMeasure: String(nativeUnitRaw),
      unitOfMeasureDisplay: unitDisplay,
    };
    const rowId = randomId();
    const newRows: FormItem[] = [];

    if (internalQtyNative > 0) {
      newRows.push({
        ...baseRow,
        tempId: `${rowId}-in`,
        quantityRequested: Number(internalQtyNative.toFixed(4)),
        isExternalItem: false,
      });
    }
    if (externalQtyNative > 0) {
      newRows.push({
        ...baseRow,
        tempId: `${rowId}-ex`,
        quantityRequested: Number(externalQtyNative.toFixed(4)),
        isExternalItem: true,
      });
    }

    if (newRows.length === 0) return;
    setItems((prev) => [...prev, ...newRows]);

    if (internalQtyNative > 0 && externalQtyNative > 0) {
      notify.success(
        `Split into ${internalQtyNative.toFixed(2)} ${unitDisplay} from store and ${externalQtyNative.toFixed(2)} ${unitDisplay} for external procurement.`
      );
    } else if (externalQtyNative > 0 && internalQtyNative === 0) {
      notify.warning(
        `${externalQtyNative.toFixed(2)} ${unitDisplay} routed to external procurement.`
      );
    }

    setDraft({
      inputId: "",
      quantityRequested: "",
      qtyUnit: "",
      procureExternally: false,
      externalQty: "",
      externalUnit: "",
    });
    setInputSearchValue("");
    setInputSearchTerm("");
    setChosenInput(null);
  };

  const handleRemoveItem = (tempId: string) =>
    setItems((prev) => prev.filter((i) => i.tempId !== tempId));

  const selectedSeedIds = new Set(items.filter((i) => i.seedBatchId).map((i) => i.seedBatchId));
  const seedBatchAlreadyAdded = selectedSeedBatches.length > 0
    && selectedSeedBatches.every((batch) => selectedSeedIds.has(batch.id));

  const handleAddCustomerSeeds = () => {
    if (!instruction.seedBatchId) return;
    if (selectedSeedBatches.length === 0) {
      notify.warning("Select at least one customer seed batch and quantity.");
      return;
    }

    const invalidBatch = selectedSeedBatches.find((batch) => {
      const qty = parseFloat(selectedSeedQuantities[batch.id]) || 0;
      return qty <= 0 || qty > (batch.quantityAvailable ?? 0);
    });
    if (invalidBatch) {
      notify.warning(
        `Check quantity for ${invalidBatch.batchNumber}. It must be greater than 0 and no more than ${formatQty(invalidBatch.quantityAvailable)} ${invalidBatch.unit}.`
      );
      return;
    }

    const batchGroupId = randomId();
    setItems((prev) => {
      const withoutSelectedSeedRows = prev.filter(
        (item) => !selectedSeedBatches.some((batch) => batch.id === item.seedBatchId)
      );
      const seedRows = selectedSeedBatches.map((batch, idx) => {
        const qty = parseFloat(selectedSeedQuantities[batch.id]) || 0;
        const owner = batch.ownerName || batch.customerName || batch.customerNameSnapshot || instructionCustomerName;
        return {
          tempId: `seed-${batchGroupId}-${idx}`,
          inputId: null,
          inputName: `Customer seeds — ${owner}`,
          quantityRequested: qty,
          unitOfMeasure: batch.unit ?? "",
          unitOfMeasureDisplay: batch.unit ?? "",
          isExternalItem: false,
          seedBatchId: batch.id,
          seedBatchNumber: batch.batchNumber,
          seedBatchCustomerName: owner,
          seedBatchAvailableQuantity: batch.quantityAvailable,
          seedBatchLotNumber: batch.lotNumber,
          seedBatchExpiryDate: batch.expiryDate,
        } satisfies FormItem;
      });
      return [...withoutSelectedSeedRows, ...seedRows];
    });
    notify.success(`${selectedSeedBatches.length} customer seed batch${selectedSeedBatches.length === 1 ? "" : "es"} added to requisition.`);
  };

  const purpose =
    `Sowing: ${(instruction as any).instructionNumber ?? instruction.id} — ${instruction.variety}`;

  const handleSubmit = () => {
    if (!requiredByDate || !departmentId) {
      notify.warning("Fill required-by date and department");
      return;
    }
    if (items.length === 0) {
      notify.warning("Add at least one item");
      return;
    }
    // Customer-seed sowings must include a row for the seed batch — otherwise the seeds
    // wouldn't go through the requisition lifecycle.
    if (instruction.seedBatchId) {
      const hasSeedRow = items.some((i) => !!i.seedBatchId);
      if (!hasSeedRow) {
        notify.warning(
          "This sowing uses customer seeds — select at least one seed batch before submitting."
        );
        return;
      }
    }

    // For company-inventory rows we stored the enum's numeric value already.
    // For customer-seed rows we stored the unit *name* (e.g. "Grams"); resolve it now
    // against the loaded enum so the backend receives the int value it expects.
    const resolveUnit = (raw: string): number => {
      const asNum = Number(raw);
      if (!Number.isNaN(asNum) && raw !== "") return asNum;
      const found = unitsQuery.data?.find((u) => u.name === raw);
      return found?.value ?? 0;
    };

    const payload = {
      requiredByDate,
      purpose,
      departmentId,
      greenhouseId: instruction.greenhouseId || undefined,
      notes,
      items: items.map((i) => ({
        // null for customer-seed lines — backend enforces XOR with seedBatchId.
        inputId: i.inputId ?? null,
        quantityRequested: i.quantityRequested,
        // Backend's CreateRequisitionItemDto.unitOfMeasure is the enum's int value.
        unitOfMeasure: resolveUnit(i.unitOfMeasure),
        sowingInstructionId: instruction.id,
        orderId: instruction.orderId,
        orderItemId: instruction.orderItemId,
        isExternalItem: i.isExternalItem,
        seedBatchId: i.seedBatchId,
      })),
    };

    createRequisition.mutate(payload as any, {
      onSuccess: () => {
        notify.success("Requisition raised for sowing");
        setItems([]);
        setNotes("");
        onSuccess?.();
        onOpenChange(false);
      },
      onError: (error) => handleApiError(error),
    });
  };

  const externalCount = items.filter((i) => i.isExternalItem).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid max-h-[90vh] w-[calc(100vw-1.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-screen max-sm:max-w-none max-sm:rounded-none sm:w-[92vw] md:w-[86vw] lg:w-[74vw] lg:max-w-[980px]"
        // The Input <Combobox> portals its popup to <body>, which Radix
        // considers "outside" the dialog. Without this guard, clicking a
        // dropdown item or unit-select item bubbles out and dismisses
        // the whole dialog. Ignore the dismiss when the event originated
        // in any Base UI / Radix portal slot.
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (
            target?.closest(
              "[data-slot=combobox-content], [data-slot=select-content], [data-slot=popover-content]"
            )
          ) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (
            target?.closest(
              "[data-slot=combobox-content], [data-slot=select-content], [data-slot=popover-content]"
            )
          ) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="border-b bg-background px-6 py-5 sm:px-8">
          <DialogTitle className="text-xl">Raise Requisition for Sowing</DialogTitle>
          <DialogDescription className="mt-2 leading-6">
            {instruction.variety} — {instruction.quantityToSow}{" "}
            {getSowingUnitLabel(instruction.unit, instruction.quantityToSow)}.
            Items marked external are routed to the store manager for
            procurement.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto overflow-x-hidden px-6 py-6 sm:px-8">
          <div className="space-y-8">
          {instruction.seedBatchId && (
            <section
              className={
                seedBatchAlreadyAdded
                  ? "rounded-lg border border-emerald-200 bg-emerald-50/80 p-5 text-sm space-y-5"
                  : "rounded-lg border border-amber-200 bg-amber-50/80 p-5 text-sm space-y-5"
              }
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <Sprout className="h-5 w-5 text-emerald-700 mt-0.5" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="text-base font-semibold">
                    Customer seeds — {instructionCustomerName}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {instruction.cropTypeName || instruction.variety} · {instruction.variety}
                    {seedBatchesByOrderQuery.isLoading
                      ? " · loading batches..."
                      : " · select one or more batches for this requisition."}
                  </div>
                </div>
                <Button
                  type="button"
                  variant={seedBatchAlreadyAdded ? "secondary" : "default"}
                  size="sm"
                  onClick={handleAddCustomerSeeds}
                  disabled={
                    selectedSeedBatches.length === 0 || seedBatchAlreadyAdded || createRequisition.isPending
                  }
                  className="shrink-0"
                >
                  {seedBatchAlreadyAdded ? "Included" : "Include selected"}
                </Button>
              </div>

              <div className="rounded-md border bg-background/80 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-16 py-3">Select</TableHead>
                      <TableHead className="py-3 sm:min-w-[190px]">Batch</TableHead>
                      <TableHead className="py-3 sm:min-w-[160px]">Owner</TableHead>
                      <TableHead className="py-3 sm:min-w-[140px]">Available</TableHead>
                      <TableHead className="w-28 py-3">Expiry</TableHead>
                      <TableHead className="w-44 py-3 text-right">Quantity Requested</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleSeedBatches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                          No approved customer seed batches found for this order, crop, and variety.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleSeedBatches.map((batch) => {
                        const checked = selectedSeedQuantities[batch.id] != null;
                        const available = batch.quantityAvailable ?? 0;
                        const owner = batch.ownerName || batch.customerName || batch.customerNameSnapshot || instructionCustomerName;
                        return (
                          <TableRow key={batch.id} className="align-top">
                            <TableCell className="py-4">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(value) => {
                                  setSelectedSeedQuantities((prev) => {
                                    const next = { ...prev };
                                    if (value) next[batch.id] = String(available);
                                    else delete next[batch.id];
                                    return next;
                                  });
                                }}
                                disabled={createRequisition.isPending || available <= 0}
                              />
                            </TableCell>
                            <TableCell className="py-4">
                              <div className="font-medium">{batch.batchNumber}</div>
                              <div className="text-xs text-muted-foreground">
                                {batch.cropTypeName || instruction.cropTypeName} · {batch.variety || instruction.variety}
                                {batch.lotNumber ? ` · Lot ${batch.lotNumber}` : ""}
                              </div>
                            </TableCell>
                            <TableCell className="py-4 text-sm">
                              {owner}
                            </TableCell>
                            <TableCell className="py-4 text-sm">
                              {formatQty(available)} {batch.unit}
                              {batch.numberOfPacks > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  {batch.numberOfPacks} pack{batch.numberOfPacks === 1 ? "" : "s"} x {batch.quantityPerPack} {batch.unit}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="py-4 text-sm">{formatDate(batch.expiryDate)}</TableCell>
                            <TableCell className="py-4">
                              <Input
                                type="number"
                                min="0"
                                max={available}
                                step="0.01"
                                value={selectedSeedQuantities[batch.id] ?? ""}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setSelectedSeedQuantities((prev) => ({
                                    ...prev,
                                    [batch.id]: raw,
                                  }));
                                }}
                                onFocus={() => {
                                  if (!checked) {
                                    setSelectedSeedQuantities((prev) => ({
                                      ...prev,
                                      [batch.id]: String(available),
                                    }));
                                  }
                                }}
                                disabled={createRequisition.isPending || available <= 0}
                                className="h-10 min-w-36 text-right"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {Object.keys(selectedSeedTotals).length > 0 && (
                <div className="rounded-md bg-background/70 px-3 py-2 text-sm text-muted-foreground">
                  Selected total:{" "}
                  {Object.entries(selectedSeedTotals)
                    .map(([unit, qty]) => `${formatQty(qty)} ${unit}`)
                    .join(" · ")}
                </div>
              )}
            </section>
          )}

          <section className="space-y-4">
            <div>
              <h3 className="text-base font-semibold">Requisition Details</h3>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
            <Field className="gap-2">
              <FieldLabel htmlFor="requiredByDate">
                Required By <span className="text-red-500">*</span>
              </FieldLabel>
              <DatePicker
                id="requiredByDate"
                value={requiredByDate}
                onChange={setRequiredByDate}
                disabled={createRequisition.isPending}
              />
            </Field>

            <Field className="gap-2">
              <FieldLabel htmlFor="department">
                Department <span className="text-red-500">*</span>
              </FieldLabel>
              <Select
                value={departmentId}
                onValueChange={setDepartmentId}
                disabled={createRequisition.isPending}
              >
                <SelectTrigger id="department" className="w-full">
                  <SelectValue placeholder="Select Department" />
                </SelectTrigger>
                <SelectContent>
                  {departmentsQuery.data?.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            </div>
          </section>

          <section className="space-y-5">
          <div className="rounded-lg border bg-card p-5 space-y-5">
            <div>
              <h3 className="text-base font-semibold">Store Items</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_140px_130px] items-end">
              <Field className="gap-2">
                <FieldLabel htmlFor="itemInput">Input</FieldLabel>
                <Combobox
                  filter={null}
                  value={draft.inputId || null}
                  onValueChange={(v) => {
                    const newId = v ?? "";
                    const chosen = filteredInputs.find((i) => i.id === newId) ?? null;
                    setChosenInput(chosen);
                    setDraft((d) => ({
                      ...d,
                      inputId: newId,
                      // Switching input resets the external split and selected units.
                      procureExternally: false,
                      externalQty: "",
                      qtyUnit: "",
                      externalUnit: "",
                    }));
                    setInputSearchValue(chosen?.inputName ?? "");
                    setInputSearchTerm(chosen?.inputName ?? "");
                  }}
                  inputValue={inputSearchValue}
                  disabled={createRequisition.isPending}
                >
                  <ComboboxInput
                    id="itemInput"
                    placeholder="Search input..."
                    showClear={!!draft.inputId}
                    className="w-full"
                    onChange={(e) => {
                      if (draft.inputId) {
                        setDraft((d) => ({ ...d, inputId: "" }));
                        setChosenInput(null);
                      }
                      setInputSearchValue(e.target.value);
                      setInputSearchTerm(e.target.value);
                    }}
                  />
                  <ComboboxContent>
                    <ComboboxList>
                      {filteredInputs.length === 0 && (
                        <ComboboxEmpty>
                          {inputsQuery.isFetching || searchPending
                            ? "Loading inputs..."
                            : inputsQuery.isError
                              ? "Could not load inputs. Try again."
                              : "No inputs found."}
                        </ComboboxEmpty>
                      )}
                      {filteredInputs.map((i) => {
                        const stock = i.currentStock ?? 0;
                        const unit = i.unitOfMeasureDisplay || i.unitOfMeasure;
                        return (
                          <ComboboxItem key={i.id} value={i.id}>
                            <span className="flex items-center justify-between gap-3 w-full">
                              <span>{i.inputName}</span>
                              <span
                                className={
                                  stock > 0
                                    ? "text-xs text-muted-foreground"
                                    : "text-xs text-amber-700"
                                }
                              >
                                {stock > 0
                                  ? `${stock} ${unit} in stock`
                                  : "out of stock"}
                              </span>
                            </span>
                          </ComboboxItem>
                        );
                      })}
                      {!searchPending && inputsQuery.hasNextPage && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full"
                          disabled={inputsQuery.isFetchingNextPage}
                          onClick={() => inputsQuery.fetchNextPage()}
                        >
                          {inputsQuery.isFetchingNextPage ? "Loading..." : "Load more inputs"}
                        </Button>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </Field>

              <Field className="gap-2">
                <FieldLabel htmlFor="itemQty">Total Qty</FieldLabel>
                <Input
                  id="itemQty"
                  type="number"
                  step="0.01"
                  value={draft.quantityRequested}
                  onChange={(e) => {
                    const newQtyRaw = parseFloat(e.target.value) || 0;
                    // Convert to native unit so the shortfall recommendation stays accurate
                    // even when the user is typing in a different unit (e.g. kg vs g).
                    const newQtyNative = convertQty(
                      newQtyRaw,
                      totalUnit,
                      nativeUnitName,
                      category
                    );
                    setDraft((d) => {
                      const newShortfall = Math.max(
                        0,
                        sameInputDraftedQty + newQtyNative - stockAvailable
                      );
                      // Keep the external recommendation synced as Total changes.
                      // Express the suggested shortfall in the user's chosen external unit.
                      const suggestedExternal =
                        d.procureExternally && newShortfall > 0
                          ? convertQty(
                              newShortfall,
                              nativeUnitName,
                              d.externalUnit || nativeUnitName,
                              category
                            ).toString()
                          : d.externalQty;
                      return {
                        ...d,
                        quantityRequested: e.target.value,
                        externalQty: suggestedExternal,
                      };
                    });
                  }}
                  disabled={createRequisition.isPending || !selectedInput}
                />
              </Field>

              <Field className="gap-2">
                <FieldLabel htmlFor="itemUnit">Unit</FieldLabel>
                <Select
                  value={totalUnit}
                  onValueChange={(v) =>
                    setDraft((d) => ({ ...d, qtyUnit: v }))
                  }
                  disabled={createRequisition.isPending || !selectedInput}
                >
                  <SelectTrigger id="itemUnit" className="w-full">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {compatibleUnits.map((u) => (
                      <SelectItem key={u.value} value={u.name}>
                        {u.abbreviation || u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {selectedInput && draftQtyNative > 0 && (() => {
              // All math is done in the input's native unit so the preview is consistent.
              const unit = nativeUnitDisplay;
              const fromStoreCap = Math.max(
                0,
                stockAvailable - sameInputDraftedQty
              );
              const partialFromStore = internalQtyNative > fromStoreCap;
              const partialShort = Math.max(0, internalQtyNative - fromStoreCap);

              return (
                <div className="space-y-4">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={draft.procureExternally}
                      onCheckedChange={(v) => {
                        const checked = !!v;
                        setDraft((d) => {
                          const targetUnit = d.externalUnit || nativeUnitName;
                          const suggested =
                            checked && autoShortfall > 0
                              ? convertQty(
                                  autoShortfall,
                                  nativeUnitName,
                                  targetUnit,
                                  category
                                ).toString()
                              : "";
                          return {
                            ...d,
                            procureExternally: checked,
                            externalQty: suggested,
                          };
                        });
                      }}
                      disabled={createRequisition.isPending}
                    />
                    <span>
                      Procure some externally
                      {isShort && (
                        <span className="ml-1 text-amber-700">
                          (recommended — short by {autoShortfall.toFixed(2)}{" "}
                          {unit})
                        </span>
                      )}
                    </span>
                  </label>

                  {draft.procureExternally && (
                    <div className="pl-6">
                      <Field className="gap-2">
                        <FieldLabel htmlFor="externalQty">
                          How much to procure externally?
                        </FieldLabel>
                        <div className="flex items-center gap-2">
                          <Input
                            id="externalQty"
                            type="number"
                            step="0.01"
                            min="0"
                            value={draft.externalQty}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                externalQty: e.target.value,
                              }))
                            }
                            disabled={createRequisition.isPending}
                            className="w-32"
                          />
                          <Select
                            value={externalUnit}
                            onValueChange={(v) =>
                              setDraft((d) => {
                                // Translate the typed value into the new unit so the
                                // amount the user sees stays the same magnitude when they
                                // change unit (e.g. 500 g → 0.5 kg).
                                const current = parseFloat(d.externalQty);
                                if (isNaN(current) || !d.externalQty) {
                                  return { ...d, externalUnit: v };
                                }
                                const converted = convertQty(
                                  current,
                                  d.externalUnit || nativeUnitName,
                                  v,
                                  category
                                );
                                return {
                                  ...d,
                                  externalUnit: v,
                                  externalQty: converted.toString(),
                                };
                              })
                            }
                            disabled={createRequisition.isPending}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {compatibleUnits.map((u) => (
                                <SelectItem key={u.value} value={u.name}>
                                  {u.abbreviation || u.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </Field>
                    </div>
                  )}

                  {/* Plain-language breakdown — always rendered in the input's native unit
                      for one consistent reference, regardless of what unit the user typed. */}
                  <div className="rounded-md bg-muted/40 border p-4 text-sm space-y-2">
                    <div className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      What will happen
                    </div>
                    {internalQtyNative > 0 && (
                      <div className="flex items-start justify-between gap-2">
                        <span>
                          📦 Store issues{" "}
                          <span className="font-semibold">
                            {Math.min(internalQtyNative, fromStoreCap).toFixed(2)}{" "}
                            {unit}
                          </span>
                          {partialFromStore && (
                            <span className="text-xs text-muted-foreground block">
                              (capped at {fromStoreCap} {unit} available)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {externalQtyNative > 0 && (
                      <div className="flex items-start justify-between gap-2">
                        <span>
                          🚚 Procured externally{" "}
                          <span className="font-semibold">
                            {externalQtyNative.toFixed(2)} {unit}
                          </span>
                          <span className="text-xs text-muted-foreground block">
                            Routed to store manager
                          </span>
                        </span>
                      </div>
                    )}
                    <div className="border-t pt-1.5 mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                      <span>Total covered</span>
                      <span className="font-medium text-foreground">
                        {(
                          Math.min(internalQtyNative, fromStoreCap) +
                          externalQtyNative
                        ).toFixed(2)}{" "}
                        of {draftQtyNative.toFixed(2)} {unit}
                      </span>
                    </div>

                    {partialFromStore && (
                      <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800 space-y-2">
                        <div className="font-medium">
                          ⚠️ {partialShort.toFixed(2)} {unit} unfulfilled
                        </div>
                        <p className="text-red-700">
                          Store can only issue {fromStoreCap} {unit}. The rest
                          won't be sourced unless you procure it externally or
                          reduce Total Qty.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 bg-white"
                            onClick={() => {
                              // Express the shortfall in the user's chosen external unit.
                              const addInExternalUnit = convertQty(
                                partialShort,
                                nativeUnitName,
                                externalUnit,
                                category
                              );
                              const currentExternalUserUnit = parseFloat(
                                draft.externalQty
                              ) || 0;
                              const newExternal = (
                                currentExternalUserUnit + addInExternalUnit
                              ).toFixed(2);
                              setDraft((d) => ({
                                ...d,
                                procureExternally: true,
                                externalQty: newExternal,
                              }));
                            }}
                          >
                            Procure {partialShort.toFixed(2)} {unit} externally
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 bg-white"
                            onClick={() => {
                              // Reduce Total to fromStoreCap, expressed in user's chosen Total unit.
                              const capInTotalUnit = convertQty(
                                fromStoreCap,
                                nativeUnitName,
                                totalUnit,
                                category
                              );
                              setDraft((d) => ({
                                ...d,
                                quantityRequested: capInTotalUnit.toString(),
                                externalQty: "0",
                              }));
                            }}
                          >
                            Reduce Total to {fromStoreCap} {unit}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="flex items-center justify-end pt-1">
              <Button
                type="button"
                onClick={handleAddItem}
                disabled={createRequisition.isPending}
                className="gap-2"
                size="sm"
              >
                <Plus className="w-4 h-4" />
                Add
              </Button>
            </div>
          </div>

          {items.length > 0 && (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="py-3 sm:min-w-[360px]">Item</TableHead>
                    <TableHead className="w-28 py-3 text-right">Qty</TableHead>
                    <TableHead className="w-28 py-3">Unit</TableHead>
                    <TableHead className="w-44 py-3">Source</TableHead>
                    <TableHead className="w-14 py-3"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.tempId} className="align-top">
                      <TableCell className="py-4">
                        <div className="font-medium leading-5">{it.inputName}</div>
                        {it.seedBatchId && (
                          <span className="mt-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-800">
                            Customer seeds · {it.seedBatchNumber}
                          </span>
                        )}
                        {it.seedBatchCustomerName && (
                          <div className="text-xs text-muted-foreground">
                            {it.seedBatchCustomerName}
                            {it.seedBatchLotNumber ? ` · Lot ${it.seedBatchLotNumber}` : ""}
                            {it.seedBatchExpiryDate ? ` · Exp ${formatDate(it.seedBatchExpiryDate)}` : ""}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-4 text-right">{it.quantityRequested}</TableCell>
                      <TableCell className="py-4">{it.unitOfMeasureDisplay}</TableCell>
                      <TableCell className="py-4">
                        {it.seedBatchId ? (
                          <Badge variant="outline">Customer batch</Badge>
                        ) : it.isExternalItem ? (
                          <Badge variant="destructive">→ Store Manager</Badge>
                        ) : (
                          <Badge variant="secondary">In store</Badge>
                        )}
                      </TableCell>
                      <TableCell className="py-3">
                        {!it.seedBatchId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(it.tempId)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {externalCount > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-md p-3 border border-amber-200">
              {externalCount} item{externalCount > 1 ? "s" : ""} flagged for
              external procurement — store manager approval required before
              fulfilment.
            </p>
          )}

          </section>

          <section className="space-y-3">
          <Field className="gap-2">
            <FieldLabel htmlFor="notes">Notes (optional)</FieldLabel>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else the store should know"
              disabled={createRequisition.isPending}
              className="min-h-24"
            />
          </Field>
          </section>
          </div>
        </div>
        <DialogFooter className="border-t bg-background px-6 py-4 sm:px-8">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={createRequisition.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createRequisition.isPending || items.length === 0
            }
          >
            {createRequisition.isPending ? "Submitting..." : "Submit Requisition"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
